export const TERM_SCAN_SUCCESS_STATES = new Set(['completed', 'succeeded', 'finished', 'reviewed']);
export const TERM_SCAN_IN_FLIGHT_STATES = new Set(['admitting', 'queued', 'running', 'pending', 'retrying', 'cancelling', 'settling']);
export const TERM_SCAN_FAILED_STATES = new Set([
  'failed',
  'error',
  'expired',
  'interrupted',
  'released',
  'cancelled',
  'canceled',
  'aborted',
]);
export const TERM_SCAN_WARNING_POLL_INTERVAL_MS = 5000;
export const BULK_TERM_SCAN_RUN_METADATA_RETRY_ATTEMPTS = 4;
export const BULK_TERM_SCAN_RUN_METADATA_RETRY_BASE_DELAY_MS = 600;
export const BULK_TRANSLATION_TARGET_CONCURRENCY = 3;

const parseHttpStatusCode = (error: any): number => {
  return Number(error?.response?.status || 0);
};

const mergeStartedRunFallback = (preflight: any, startedRun: any | null): any => {
  if (!startedRun?.runId) return preflight;
  const next = preflight ? { ...preflight } : {};
  const latestRun = next?.latestRun ? { ...next.latestRun } : {};
  if (!latestRun.runId) latestRun.runId = startedRun.runId;
  if (!latestRun.lockKey && startedRun.lockKey) latestRun.lockKey = startedRun.lockKey;
  if (!latestRun.status && startedRun.status) latestRun.status = startedRun.status;
  next.latestRun = latestRun;
  if (!next.status && startedRun.status) next.status = startedRun.status;
  return next;
};

export const runBulkTermScanAutoRunPreflight = async ({
  initialPreflight,
  fetchPreflight,
  startTermScan,
  autoApplyConfident,
  metadataRetryAttempts = BULK_TERM_SCAN_RUN_METADATA_RETRY_ATTEMPTS,
  metadataRetryBaseDelayMs = BULK_TERM_SCAN_RUN_METADATA_RETRY_BASE_DELAY_MS,
  sleep = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms)),
}: {
  initialPreflight: any;
  fetchPreflight: () => Promise<any>;
  startTermScan: () => Promise<any>;
  autoApplyConfident: (runId: string) => Promise<void>;
  metadataRetryAttempts?: number;
  metadataRetryBaseDelayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<any> => {
  let preflight = initialPreflight;
  let startedRun: any | null = null;
  const withStartedRunFallback = (value: any) => mergeStartedRunFallback(value, startedRun);

  const waitForRunMetadata = async () => {
    let lastPreflight: any = withStartedRunFallback(preflight);
    let lastError: any = null;
    for (let attempt = 0; attempt < metadataRetryAttempts; attempt += 1) {
      try {
        lastPreflight = withStartedRunFallback(await fetchPreflight());
        if (lastPreflight?.latestRun?.runId) return lastPreflight;
      } catch (error: any) {
        lastError = error;
      }
      if (attempt < metadataRetryAttempts - 1) {
        await sleep(metadataRetryBaseDelayMs * (attempt + 1));
      }
    }
    if (lastPreflight) return lastPreflight;
    if (lastError) throw lastError;
    return withStartedRunFallback(preflight);
  };

  if (!preflight) preflight = await fetchPreflight();

  const preflightStatus = (preflight?.status || '').toString().trim().toLowerCase();
  if (preflightStatus === 'missing' || preflightStatus === 'failed') {
    try {
      const startResponse = await startTermScan();
      startedRun = startResponse?.data?.run || startResponse?.run || null;
    } catch (error: any) {
      if (parseHttpStatusCode(error) !== 409) throw error;
    }
    preflight = await waitForRunMetadata();
  } else if (!preflight?.latestRun?.runId) {
    preflight = await waitForRunMetadata();
  }

  if (preflight?.status === 'completed_not_applied' && preflight?.latestRun?.runId) {
    await autoApplyConfident(preflight.latestRun.runId);
    preflight = await fetchPreflight();
  }

  return withStartedRunFallback(preflight);
};

export const buildTranslationRequestPayload = ({
  userId,
  bookId,
  chapterGuid,
  sourceLang,
  targetLang,
  model,
  version,
  genres,
  intendedAudience,
  quotationStyle,
  frenchTone,
  overwriteFlag,
  enforceFrenchEmDash,
  useBatch,
  workflowMode,
  frenchDialogueRulesetVersion,
  enableSystemProofreader,
  requireCharacterAnalysis,
  expectedChapterVersion,
}: {
  userId: string;
  bookId: string;
  chapterGuid: string;
  sourceLang: string;
  targetLang: string;
  model: string;
  version: string;
  genres: string[];
  intendedAudience: string[];
  quotationStyle: string;
  frenchTone: string;
  overwriteFlag: boolean;
  enforceFrenchEmDash: boolean;
  useBatch: boolean;
  workflowMode: string;
  frenchDialogueRulesetVersion?: string;
  enableSystemProofreader: boolean;
  requireCharacterAnalysis: boolean;
  expectedChapterVersion?: string | number | null;
}) => ({
  userId,
  bookId,
  chapterGuid,
  sourceLang,
  targetLang,
  model,
  version,
  genre: genres.join(', '),
  intendedAudience,
  quotationStyle,
  tone: targetLang === 'French' ? frenchTone : '',
  overwriteFlag,
  enforceFrenchEmDash,
  useBatch,
  workflowMode,
  frenchDialogueRulesetVersion,
  enableSystemProofreader,
  requireCharacterAnalysis,
  expectedChapterVersion,
});

export const buildBulkTranslationRequestPayload = ({
  userId,
  bookId,
  sourceLang,
  targetLang,
  model,
  version,
  genres,
  quotationStyle,
  enforceFrenchEmDash,
  intendedAudience,
  frenchTone,
  useBatch,
  workflowMode,
  frenchDialogueRulesetVersion,
  enableSystemProofreader,
  requireCharacterAnalysis,
  preflightDecision,
  preflightState,
  chapters,
}: {
  userId: string;
  bookId: string;
  sourceLang: string;
  targetLang: string;
  model: string;
  version: string;
  genres: string[];
  quotationStyle: string;
  enforceFrenchEmDash: boolean;
  intendedAudience: string[];
  frenchTone: string;
  useBatch: boolean;
  workflowMode: string;
  frenchDialogueRulesetVersion?: string;
  enableSystemProofreader: boolean;
  requireCharacterAnalysis: boolean;
  preflightDecision: 'auto_run' | 'skip';
  preflightState: any | null;
  chapters: Array<{
    chapterGuid: string;
    expectedChapterVersion?: string | number | null;
    overwriteFlag?: boolean;
  }>;
}) => ({
  userId,
  bookId,
  sourceLang,
  targetLang,
  model,
  version,
  genre: genres.join(', '),
  quotationStyle,
  overwriteFlag: false,
  enforceFrenchEmDash,
  intendedAudience,
  tone: targetLang === 'French' ? frenchTone : '',
  useBatch,
  workflowMode,
  frenchDialogueRulesetVersion,
  enableSystemProofreader,
  requireCharacterAnalysis,
  runAnalysis: false,
  prewarmCache: true,
  termScanPreflightDecision: preflightDecision,
  termScanSkipped: preflightDecision === 'skip',
  termScanAutoRun: preflightDecision === 'auto_run',
  termScanRunId: preflightState?.latestRun?.runId || null,
  termScanStatus: preflightState?.status || null,
  termScanLockKey: preflightState?.latestRun?.lockKey || null,
  chapters,
});
